/* eslint-disable import/order */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { func, shape } from 'prop-types';
import * as R from 'ramda';
import { Button, useMediaQuery } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import AddRoundedIcon from '@mui/icons-material/AddRounded';

// Redux
import newSupplierAuthSteps from 'feature/loading-management/supplier-authorizations/create-new-steps/Steps';

// Components
import Wizard from 'common/Wizard';
import FilterableDataGrid from 'common/templates/FilterableDataGrid';
import Sidebar from './sidebar/Sidebar.connector';
import CardsPanel from './cards-panel/CardsPanel.connector';
import MobileFilterDrawer from './mobile-filter-drawer/MobileFilterDrawer.connector';
import MobileDrawer from '../mobile-drawer/MobileDrawer.connector';
import SupplierAuthDialog from './confirmation-dialog/ConfirmationDialog.connector';
import SupplierGridView from './list-view/SupplierGridView';
import { useUserRolePermissions } from 'hooks';
import SupplierAuthorizationsFilters from './list-view/SupplierAuthorizationsFilters';
import useSupplierAuthFilters from './list-view/useSupplierAuthFilters';
import { useGetUniqueAffiliateSuppliersListQuery } from './list-view/supplierAuthorizationsEndpoints';

// Utils
import { EvaluateFlag } from 'common/FeatureFlags/Flag';
import { DRAWER_TYPES } from 'utils/constants';
import { DrawerContext } from 'common/drawer/DrawerContext';
import {
  confirmCloseDialog,
  resetSupplierAuthWizardData,
} from './store/createNewSupplierAuthSlice';
import { openConfirmationDialog } from 'store/Confirmationdialogslice';

const SupplierAuthorizations = ({
  dialogDetails: { message, params },
  setActiveSupplierAuthTab,
  resetFilters,
  setMobileFilterDrawerOpen,
  setSupplierAuthorizationsPending,
}) => {
  const dispatch = useDispatch();
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const { viewBy } = useParams();
  const supplierListFlag = EvaluateFlag(process.env.REACT_APP_LD_SUPPLIER_AUTH_LIST);
  const { isProductLoadingManage } = useUserRolePermissions();
  const [isExporting, setIsExporting] = useState(false);

  const { setDrawerComponentProps } = useContext(DrawerContext);

  // Use the custom hook to manage filter state
  const {
    appliedFilters,
    isOnDefaultState,
    draftFilters,
    handleApply,
    handleReset,
    hasChanges,
    setAffiliateFilter,
    setStatusFilter,
    setLocationFilter,
    setSearch,
    setSupplierFilter,
    setProductFilter,
  } = useSupplierAuthFilters();

  // Fetch all supplier options for the selected affiliate
  const supplierParams = {
    'Affiliate.CompanyCustomerIdName': appliedFilters?.affiliate?.customerIdName,
    'Affiliate.DataCompanyId': appliedFilters?.affiliate?.dataCompanyId,
  };

  const { data: supplierData = [] } = useGetUniqueAffiliateSuppliersListQuery(supplierParams, {
    skip: !appliedFilters?.affiliate?.customerIdName || !appliedFilters?.affiliate?.dataCompanyId,
  });

  const supplierOptions = useMemo(() => {
    const getSupplierIdName = supplier =>
      supplier?.companyCustomerIdName ?? supplier?.idName ?? supplier?.customerIdName ?? null;

    const getSupplierLabel = supplier => supplier?.label ?? supplier?.nameLong;

    const mapSupplierOption = supplier => {
      const companyCustomerIdName = getSupplierIdName(supplier);
      const dataCompanyId = supplier?.dataCompanyId;

      if (!companyCustomerIdName || dataCompanyId === null || dataCompanyId === undefined) {
        return null;
      }

      return {
        companyCustomerIdName,
        dataCompanyId,
        label: getSupplierLabel(supplier),
        value: companyCustomerIdName,
      };
    };

    return supplierData.map(mapSupplierOption).filter(Boolean);
  }, [supplierData]);

  useEffect(() => {
    return () => {
      setActiveSupplierAuthTab('baseProducts');
      // set supplierAuthPending to default so when switching away viewBy
      // does not incorrectly render or crash application
      setSupplierAuthorizationsPending(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => resetFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewBy]);

  const openUnsavedChangesDialog = () =>
    openConfirmationDialog({
      confirmAction: () => dispatch(confirmCloseDialog()),
      header: 'Unsaved Changes',
      leftButtonActionType: 'confirm',
      leftButtonText: 'Continue',
      message:
        'Are you sure you want to leave this page? You have unsaved Supplier Authorization changes.',
      params: {},
      rightButtonActionType: 'cancel',
      rightButtonText: 'Keep Editing',
    });

  const handleActivationClick = () => {
    setDrawerComponentProps({
      Component: props => (
        <Wizard
          stepHeaderSx={{ pb: 1 }}
          steps={newSupplierAuthSteps}
          title="New Supplier Authorizations"
          {...props}
        />
      ),
      forceCloseSelector: state => state.loadingManagement.createNewSupplierAuth.forceClose,
      holdOpenSelector: state =>
        state.loadingManagement.createNewSupplierAuth.hasUnsavedChanges ||
        state.loadingManagement.createNewSupplierAuth.submittingBatch,
      resetWizard: resetSupplierAuthWizardData,
      setCurrentDialogOpen: openUnsavedChangesDialog,
      type: DRAWER_TYPES.createNewSupplierAuth,
    });
  };

  const handleNewAuthClick = () => {
    setDrawerComponentProps({
      Component: props => (
        <Wizard
          stepHeaderSx={{ pb: 1 }}
          steps={newSupplierAuthSteps}
          title="New Supplier Authorizations"
          {...props}
        />
      ),
      forceCloseSelector: state => state.loadingManagement.createNewSupplierAuth.forceClose,
      holdOpenSelector: state =>
        state.loadingManagement.createNewSupplierAuth.hasUnsavedChanges ||
        state.loadingManagement.createNewSupplierAuth.submittingBatch,
      resetWizard: resetSupplierAuthWizardData,
      setCurrentDialogOpen: openUnsavedChangesDialog,
      type: DRAWER_TYPES.createNewSupplierAuth,
    });
  };

  return (
    <>
      {supplierListFlag ? (
        <FilterableDataGrid
          filterContent={
            <SupplierAuthorizationsFilters
              affiliateFilter={draftFilters.affiliate}
              isExporting={isExporting}
              locationFilter={draftFilters.location}
              productFilter={draftFilters.product}
              setAffiliateFilter={setAffiliateFilter}
              setLocationFilter={setLocationFilter}
              setProductFilter={setProductFilter}
              setStatusFilter={setStatusFilter}
              setSupplierFilter={setSupplierFilter}
              statusFilter={draftFilters.status}
              supplierFilter={draftFilters.supplier}
            />
          }
          handleReset={handleReset}
          hasChanges={hasChanges}
          headerTitle="Supplier Authorizations"
          isExporting={isExporting}
          isOnDefaultState={isOnDefaultState}
          onApply={handleApply}
          onSearchChange={setSearch}
          primaryFilterActionButtonLabel="Apply Filters"
          searchValue={draftFilters.search}
          secondaryFilterActionButtonLabel="Reset Filters"
          titleMenu={
            <>
              {isProductLoadingManage && (
                <Button
                  onClick={handleNewAuthClick}
                  startIcon={<AddRoundedIcon />}
                  variant="vertical-with-icon-contained"
                >
                  New Authorizations
                </Button>
              )}
            </>
          }
        >
          <SupplierGridView
            appliedFilters={appliedFilters}
            onExportingChange={setIsExporting}
            supplierOptions={supplierOptions}
          />
        </FilterableDataGrid>
      ) : (
        <>
          <Sidebar />
          {lgUp ? (
            <CardsPanel />
          ) : (
            <>
              <MobileDrawer
                activationClick={handleActivationClick}
                filtersClick={() => setMobileFilterDrawerOpen(true)}
                panel={<CardsPanel />}
                type={viewBy}
              />
              <MobileFilterDrawer />
            </>
          )}
        </>
      )}
      {!R.isNil(params) && message && <SupplierAuthDialog />}
    </>
  );
};

SupplierAuthorizations.propTypes = {
  dialogDetails: shape({}).isRequired,
  resetFilters: func.isRequired,
  setActiveSupplierAuthTab: func.isRequired,
  setMobileFilterDrawerOpen: func.isRequired,
  setSupplierAuthorizationsPending: func.isRequired,
};

export default SupplierAuthorizations;
